import "./style.scss";

import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { Button, InputAdornment } from "@mui/material";
import BlockModal from "components/shared/BlockModal";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import PeriodStatusChip from "components/shared/PeriodStatusChip";
import SaveButton from "components/shared/SaveButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { currenciesList } from "currenciesList";
import { formatLocaleDateString, getCurrencyData } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";
import type { TPeriod } from "store/periodStore";
import { fetchPeriodExchangeRates, fetchPeriods, putPeriodExchangeRates } from "store/periodStore";
import type { Currencies } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { PeriodActiveStatus } from "store/types";

import variables from "../../assets/styles/_variables.module.scss";
import CurrencyRatesModal from "./CurrencyRatesModal";

const CurrencySettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { activePeriod, periodExchangeRates, allPeriods } = useAppSelector((state) => state.period);
    const { companyInfos } = useCompanySettings();
    const currency = companyInfos?.currency;
    const [exchangeRates, setExchangeRates] = useState<{ currency: Currencies; rate: number; error: boolean }[]>([]);
    const [showCurrencyRate, setShowCurrencyRate] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const blocker = useBlocker(isDirty);
    const [currentPeriod, setCurrentPeriod] = useState<Partial<TPeriod>>({
        id: -1,
        periodStart: new Date(),
        periodEnd: new Date(),
    });

    const hasErrors = exchangeRates.some((rate) => rate.error);

    const handleOnPeriodChange = (e: any, value: any) => {
        setCurrentPeriod(value);
        dispatch(fetchPeriodExchangeRates({ periodId: value.id }));
    };

    const handleExchangeRateChange = (e: any) => {
        setIsDirty(true);
        const error = !/^[0-9.]*$/.test(e.target.value);
        setExchangeRates((prev) => {
            const newExchangeRates = [...prev];
            const index = newExchangeRates.findIndex((rate) => rate.currency == e.target.name);
            if (index == -1) newExchangeRates.push({ currency: e.target.name, rate: e.target.value, error });
            else {
                newExchangeRates[index].rate = e.target.value;
                newExchangeRates[index].error = error;
            }
            return newExchangeRates;
        });
    };

    useEffect(() => {
        dispatch(fetchPeriods()).then((res) => {
            const defaultPeriod = res.payload.filter((period) => period.isActive !== PeriodActiveStatus.PENDING)[0];
            setCurrentPeriod(defaultPeriod);
            dispatch(fetchPeriodExchangeRates({ periodId: defaultPeriod.id }));
        });
    }, []);

    const handleOnSave = () => {
        dispatch(putPeriodExchangeRates({ periodId: currentPeriod.id ?? -1, exchangeRates }));
        setIsDirty(false);
        blocker.reset?.();
    };

    const renderInputPeriod = (option: any) => {
        return (
            <StyledTextField
                {...option}
                InputProps={{
                    ...option.InputProps,
                    startAdornment: (
                        <InputAdornment position="end" className="start-adornment">
                            <div className="adornment-wrapper">
                                <DateRangeOutlinedIcon style={{ height: "15px", width: "15px", color: "#717171" }} />
                                <span>{t("Settings.RatesPeriod")}</span>
                            </div>
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <>
                            <div className="end-adornment">
                                {typeof currentPeriod?.isActive === "number" && (
                                    <PeriodStatusChip periodStatus={currentPeriod.isActive} />
                                )}
                            </div>
                            <>{option.InputProps.endAdornment}</>
                        </>
                    ),
                }}
            />
        );
    };

    const renderPeriodOption = (props: any, option: any) => {
        return (
            <li {...props} key={option.id} className="currencies-title-options-list rates-periods-list">
                <div className="rates-option-wrapper">
                    <div className="range-dates-wrapper">
                        <span>{formatLocaleDateString({ date: option.periodStart })}</span>
                        <span>↔</span>
                        <span>{formatLocaleDateString({ date: option.periodEnd })}</span>
                    </div>
                    <div className="rates-option-end">
                        <PeriodStatusChip periodStatus={option.isActive} />
                    </div>
                </div>
            </li>
        );
    };

    const onOpenCurrencyModal = (currency: string) => {
        setShowCurrencyRate(currency);
    };

    return (
        <div className="export-settings currency-settings">
            <BlockModal blocker={blocker} />
            {!!showCurrencyRate && (
                <CurrencyRatesModal
                    showCurrencyRate={showCurrencyRate}
                    close={() => setShowCurrencyRate(null)}
                    setExchangeRates={setExchangeRates}
                    companyCurrency={currency}
                    setIsDirty={setIsDirty}
                />
            )}
            <div className="currency-settings-header">
                <h1 className="currency-settings-title">{t("Settings.Currency")}</h1>
                <div className="currency-settings-header-bottom">
                    <span className="currency-settings-subtitle">{`${t("Settings.ExchangeRateSubtitle")} ${
                        periodExchangeRates.length
                    } `}</span>
                    <div className="reference-currency-wrapper">
                        <span>{t("Settings.ReferenceCurrency")}</span>
                        <div className="reference-currency">
                            <img src={getCurrencyData(currency ?? "EUR")?.flag} />
                            <span>1.00 {getCurrencyData(currency ?? "EUR")?.code}</span>
                        </div>
                    </div>
                </div>
            </div>
            <AutocompleteFormComponent
                options={allPeriods}
                value={currentPeriod}
                onChange={handleOnPeriodChange}
                defaultFilterOptions
                className="currency-settings-period-field"
                renderInput={renderInputPeriod}
                renderOption={renderPeriodOption}
                disableClearable
                renderOptionText={(option: TPeriod) =>
                    formatLocaleDateString({ date: option.periodStart }) +
                    "  ↔  " +
                    formatLocaleDateString({ date: option.periodEnd })
                }
            />
            <div className="currency-settings-exchange-rate-wrapper ">
                <div className="exchange-rate-list">
                    {periodExchangeRates &&
                        periodExchangeRates.map((rate, i) => (
                            <div className="setting-buttons" key={i}>
                                <div className="setting-left">
                                    <div className="currency-exchange-wrapper">
                                        <span>{currency}</span>
                                        <SwapHorizOutlinedIcon
                                            style={{
                                                color: variables["foreground"],
                                                height: "20px",
                                                width: "20px",
                                                opacity: "0.7",
                                            }}
                                        />
                                        <div className="currency-name-wrapper">
                                            <img className="currency-flag" src={getCurrencyData(rate.currency)?.flag} />
                                            <span className="currency-code">{rate.currency}</span>
                                            <span className="currency-name">
                                                /{getCurrencyData(rate.currency)?.name}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="setting-right">
                                    {currency === "EUR" && (
                                        <Button
                                            variant="text"
                                            sx={{
                                                color: "#4C93E6",
                                                fontSize: "11px",
                                                fontWeight: "300",
                                                textTransform: "none",
                                                fontFamily: "Poppins",
                                            }}
                                            onClick={() => {
                                                onOpenCurrencyModal(rate.currency);
                                            }}
                                        >
                                            <CurrencyExchangeOutlinedIcon
                                                style={{ height: "13px", width: "13px", marginRight: "0.25rem" }}
                                            />
                                            {t("Settings.ShowRates")}
                                        </Button>
                                    )}
                                    <StyledTextField
                                        name={rate.currency}
                                        defaultValue={rate.rate}
                                        onChange={handleExchangeRateChange}
                                        disabled={!currentPeriod.isActive}
                                        id={`currency-field-${rate.currency}`}
                                        className="currency-rate-field"
                                        style={{ maxWidth: "150px" }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <span className="end-adornment">{rate.currency}</span>
                                                </InputAdornment>
                                            ),
                                        }}
                                        error={exchangeRates.find((item) => item.currency == rate.currency)?.error}
                                    />
                                </div>
                            </div>
                        ))}
                </div>
            </div>
            {isDirty && !hasErrors && <SaveButton buttonText={t("Settings.SaveChanges")} onSave={handleOnSave} />}
        </div>
    );
};

export default CurrencySettings;
